import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    const stored = localStorage.getItem('orb_theme');
    if (stored) return stored;
    // Default to light mode for new users
    return 'light';
  });

  // iter122 — desktop view mode. 'collapsed' = 4xl-wide centered
  // content with side padding (matches UserAdminPage). 'expanded' =
  // legacy full-viewport content. Persisted per-device. CSS rules in
  // App.css are gated behind `html[data-desktop-view="collapsed"]`
  // so the layout flips synchronously with this attribute.
  const [desktopView, setDesktopView] = useState(() => {
    const stored = localStorage.getItem('orb_desktop_view');
    return stored === 'expanded' ? 'expanded' : 'collapsed';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('orb_theme', theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.setAttribute('data-desktop-view', desktopView);
    localStorage.setItem('orb_desktop_view', desktopView);
  }, [desktopView]);

  // iter125 — Hydrate from the logged-in user's saved preferences.
  // Listens to a custom event the AuthContext dispatches with the
  // user's `theme` + `desktop_view` whenever auth state is loaded.
  // Falls back to localStorage if user has no stored preference yet.
  useEffect(() => {
    const handler = (e) => {
      const prefs = e?.detail || {};
      if (prefs.theme === 'light' || prefs.theme === 'dark') {
        setTheme(prefs.theme);
      }
      if (prefs.desktop_view === 'collapsed' || prefs.desktop_view === 'expanded') {
        setDesktopView(prefs.desktop_view);
      }
    };
    window.addEventListener('orb-user-prefs-loaded', handler);
    return () => window.removeEventListener('orb-user-prefs-loaded', handler);
  }, []);

  // iter125.2 — On change, also push to the backend so the
  // preference is persisted to the user's profile (and not just the
  // device's localStorage). The correct endpoint is `PUT /api/auth/me`
  // (NOT /users/me — that route doesn't exist; previous wiring was
  // hitting a 404 which is why theme changes weren't sticking across
  // refresh/logout/login). The localStorage write above happens
  // regardless so a network blip won't lose the user's local choice.
  const persistRemote = (patch) => {
    const token = localStorage.getItem('orb_token');
    if (!token) return;
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backend}/api/auth/me`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(patch),
    }).catch(() => {});
  };

  const toggleTheme = () => {
    setTheme((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      persistRemote({ theme: next });
      return next;
    });
  };

  const toggleDesktopView = () => {
    setDesktopView((prev) => {
      const next = prev === 'collapsed' ? 'expanded' : 'collapsed';
      persistRemote({ desktop_view: next });
      return next;
    });
  };

  const setThemeAndPersist = (next) => {
    setTheme(next);
    persistRemote({ theme: next });
  };
  const setDesktopViewAndPersist = (next) => {
    setDesktopView(next);
    persistRemote({ desktop_view: next });
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme: setThemeAndPersist, toggleTheme, desktopView, setDesktopView: setDesktopViewAndPersist, toggleDesktopView }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
